/**
 * PATH: apps/quote/src/pages/pricelist-page/WorkbookTab.tsx
 * FILE_ID: QUOTE-PRICELIST-WORKBOOK-TAB-001
 * PURPOSE: Quote Hub primary pricelist surface — the shared universal Excel-like
 *          grid (@quotech/pricelist). Self-contained: pick a pricelist, create a
 *          new one, then add industry-template sections + custom columns inside.
 * WHY: One pricelist editor across the master SPA, Quote Hub and SAM/Newmark;
 *      structure (Steel, Waterproofing, …) is data, not bespoke code.
 * RELATED: @quotech/pricelist, lib/setupApi (getApiClient), PricelistPage
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Loader2 } from 'lucide-react'
import { PricelistWorkbook, canArrangePricelistSchema, createPricelistClient } from '@quotech/pricelist'
import { createPricelistIqClient, usePricelistIq } from '@quotech/quote-builder'
import { useAuth, useComputedIsAdmin } from '@quotech/auth'
import { getApiClient } from '@/lib/setupApi'

interface PricebookOption {
  id: string
  name: string
  is_default?: boolean
}

interface WorkbookTabProps {
  pricebooks: PricebookOption[]
  activePricebookId?: string
  onSelectPricebook: (id: string) => void
}

export default function WorkbookTab({ pricebooks, activePricebookId, onSelectPricebook }: WorkbookTabProps) {
  // getApiClient() baseURL already ends in `/api` (the shared @quotech/api
  // client), so pass `/quotebuilder` — NOT the package default
  // `/api/quotebuilder` — to avoid a doubled `/api/api/quotebuilder/...` 404.
  const client = useMemo(() => createPricelistClient(getApiClient(), '/quotebuilder'), [])
  const iqClient = useMemo(() => createPricelistIqClient(getApiClient()), [])
  const iq = usePricelistIq(iqClient)
  const queryClient = useQueryClient()
  const { user } = useAuth()
  // Task 9: price master is read-only unless an authorised role enters edit
  // mode. user.role in the auth store is not always the OrganisationMember
  // role (org owners were falsely view-only), so live RBAC is the authority;
  // the static role/email check stays as the allowlisted-email path.
  const computedIsAdmin = useComputedIsAdmin(user as unknown as Record<string, unknown> | null)
  const canEdit = computedIsAdmin || canArrangePricelistSchema(user?.role, user?.email)
  const [creating, setCreating] = useState(false)

  const createPricebook = async () => {
    const name = window.prompt('Name your pricelist (e.g. "2026 Pricelist", "Commercial")')?.trim()
    if (!name) return
    setCreating(true)
    try {
      const created = await client.createPricebook(name)
      await queryClient.invalidateQueries({ queryKey: ['pricebooks'] })
      if (created?.id) onSelectPricebook(created.id)
    } catch {
      window.alert('Could not create the pricelist. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500 dark:text-gray-400">Pricelist</label>
          <select
            value={activePricebookId ?? ''}
            onChange={(e) => e.target.value && onSelectPricebook(e.target.value)}
            disabled={!pricebooks.length}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            {!pricebooks.length && <option value="">No pricelists yet</option>}
            {pricebooks.map((pb) => (
              <option key={pb.id} value={pb.id}>
                {pb.name}
                {pb.is_default ? ' (Default)' : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={createPricebook}
          disabled={creating}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          New pricelist
        </button>
      </div>

      {activePricebookId ? (
        <div className="min-h-[460px]">
          {/* pricingColumns=false — the pricelist is supplier COST data only;
              markup/sell live in the quote builder (2026-07-08 meeting). */}
          <PricelistWorkbook
            key={activePricebookId}
            client={client}
            pricebookId={activePricebookId}
            editable={canEdit}
            requireEditMode
            pricingColumns={false}
            iqEnabled={iq.enabled}
          />
        </div>
      ) : (
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>No pricelist yet. Create one, then add industry-template sections inside it.</p>
          <button
            onClick={createPricebook}
            disabled={creating}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create your first pricelist
          </button>
        </div>
      )}
    </div>
  )
}
